import {
  type McapVirtualGrant,
  type McapVirtualLeaseRefreshNeeded,
  type McapVirtualWorkerAcknowledgement,
  getMcapVirtualSessionNonce,
  mcapVirtualGrantKey,
} from './protocol';

const MCAP_VIRTUAL_WORKER_URL = '/mcap-virtual-sw.js';

const MCAP_VIRTUAL_CONTROL_RELOAD_KEY = 'vll-mcap-worker-control-reload';
const MCAP_VIRTUAL_ACK_TIMEOUT_MS = 5_000;

interface WorkerTarget {
  postMessage(message: unknown, transfer?: Transferable[]): void;
}

interface WorkerRegistration {
  active: WorkerTarget | null;
  waiting: WorkerTarget | null;
  installing: WorkerTarget | null;
}

export interface McapVirtualServiceWorkerContainer {
  readonly controller: WorkerTarget | null;
  readonly ready: Promise<WorkerRegistration>;
  register(scriptUrl: string, options: { scope: string }): Promise<WorkerRegistration>;
  addEventListener(type: 'controllerchange', listener: EventListener): void;
  addEventListener(type: 'message', listener: EventListener): void;
}

export type McapVirtualLeaseRefreshListener = (notice: McapVirtualLeaseRefreshNeeded) => void;

export interface McapVirtualWorkerControlRecovery {
  tryReload(): boolean;
  clearReloadMarker(): void;
}

class McapVirtualWorkerControlError extends Error {
  constructor() {
    super(
      'The MCAP playback service worker did not take control. Reload the page to enable playback.',
    );
    this.name = 'McapVirtualWorkerControlError';
  }
}

export interface McapVirtualMcapPageApi {
  readonly sessionNonce: string;
  register(grant: McapVirtualGrant): Promise<void>;
  update(grant: McapVirtualGrant): Promise<void>;
  release(sourceId: string): void;
  clear(): void;
  onLeaseRefresh(listener: McapVirtualLeaseRefreshListener): () => void;
}

function browserServiceWorkerContainer(): McapVirtualServiceWorkerContainer | undefined {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
  return navigator.serviceWorker as unknown as McapVirtualServiceWorkerContainer;
}

function browserWorkerControlRecovery(): McapVirtualWorkerControlRecovery | undefined {
  if (typeof window === 'undefined') return undefined;
  return {
    tryReload: () => {
      try {
        if (window.sessionStorage.getItem(MCAP_VIRTUAL_CONTROL_RELOAD_KEY) === '1') return false;
        window.sessionStorage.setItem(MCAP_VIRTUAL_CONTROL_RELOAD_KEY, '1');
        window.location.reload();
        return true;
      } catch {
        return false;
      }
    },
    clearReloadMarker: () => {
      try {
        window.sessionStorage.removeItem(MCAP_VIRTUAL_CONTROL_RELOAD_KEY);
      } catch {
        // Storage can be unavailable in a privacy-restricted browser context.
      }
    },
  };
}

function isLeaseRefreshNotice(value: unknown): value is McapVirtualLeaseRefreshNeeded {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<McapVirtualLeaseRefreshNeeded>;
  return (
    message.type === 'vll-mcap-lease-refresh-needed' &&
    typeof message.sessionNonce === 'string' &&
    typeof message.sourceId === 'string'
  );
}

/**
 * Page-side memory registry for virtual MCAP grants. It replays its complete
 * registry when a Service Worker controller restarts, but never serializes a
 * lease to browser storage.
 */
export class McapVirtualMcapController implements McapVirtualMcapPageApi {
  readonly sessionNonce: string;

  private readonly grants = new Map<string, McapVirtualGrant>();
  private readonly leaseRefreshListeners = new Set<McapVirtualLeaseRefreshListener>();
  private readonly controllerChangeListeners = new Set<() => void>();
  private registration: WorkerRegistration | undefined;
  private startPromise: Promise<void> | undefined;

  constructor(
    private readonly serviceWorkers = browserServiceWorkerContainer(),
    sessionNonce = getMcapVirtualSessionNonce(),
    private readonly workerControlRecovery = browserWorkerControlRecovery(),
  ) {
    this.sessionNonce = sessionNonce;
  }

  start(): Promise<void> {
    if (!this.serviceWorkers) return Promise.resolve();
    this.startPromise ??= this.startWorker().catch((error: unknown) => {
      this.startPromise = undefined;
      throw error;
    });
    return this.startPromise;
  }

  async register(grant: McapVirtualGrant): Promise<void> {
    this.assertLocalGrant(grant);
    const key = mcapVirtualGrantKey(grant.sessionNonce, grant.sourceId);
    this.grants.set(key, grant);
    try {
      await this.start();
    } catch (error) {
      throw new Error('mcap_virtual_worker_unavailable', { cause: error });
    }
    // A newer update or release can win while the worker is starting. Do not let
    // this suspended call reinstall an obsolete grant after that decision.
    if (this.grants.get(key) !== grant) return;
    await this.postWithAcknowledgement(
      { type: 'vll-mcap-register', grant },
      () => this.grants.get(key) === grant,
    );
  }

  async update(grant: McapVirtualGrant): Promise<void> {
    await this.register(grant);
  }

  release(sourceId: string): void {
    const key = mcapVirtualGrantKey(this.sessionNonce, sourceId);
    if (!this.grants.delete(key)) return;
    this.post({ type: 'vll-mcap-release', sessionNonce: this.sessionNonce, sourceId });
  }

  clear(): void {
    for (const grant of this.grants.values()) {
      this.post({
        type: 'vll-mcap-release',
        sessionNonce: grant.sessionNonce,
        sourceId: grant.sourceId,
      });
    }
    this.grants.clear();
  }

  onLeaseRefresh(listener: McapVirtualLeaseRefreshListener): () => void {
    this.leaseRefreshListeners.add(listener);
    return () => this.leaseRefreshListeners.delete(listener);
  }

  private async startWorker(): Promise<void> {
    const serviceWorkers = this.serviceWorkers;
    if (!serviceWorkers) return;
    serviceWorkers.addEventListener('controllerchange', this.handleControllerChange);
    serviceWorkers.addEventListener('message', this.receiveMessage);
    this.registration = await serviceWorkers.register(MCAP_VIRTUAL_WORKER_URL, { scope: '/' });
    this.registration = await serviceWorkers.ready;
    if (!serviceWorkers.controller) {
      if (this.workerControlRecovery?.tryReload()) return;
      throw new McapVirtualWorkerControlError();
    }
    this.workerControlRecovery?.clearReloadMarker();
    this.syncGrants();
  }

  private assertLocalGrant(grant: McapVirtualGrant): void {
    if (grant.sessionNonce !== this.sessionNonce) {
      throw new Error('A virtual MCAP grant must use this page session nonce');
    }
  }

  private readonly syncGrants = (): void => {
    for (const grant of this.grants.values()) this.post({ type: 'vll-mcap-register', grant });
  };

  private readonly handleControllerChange = (): void => {
    if (!this.serviceWorkers?.controller) {
      this.workerControlRecovery?.tryReload();
      return;
    }
    this.workerControlRecovery?.clearReloadMarker();
    this.syncGrants();
    for (const listener of this.controllerChangeListeners) listener();
  };

  private readonly receiveMessage = (event: Event): void => {
    const messageEvent = event as MessageEvent<unknown>;
    if (
      !isLeaseRefreshNotice(messageEvent.data) ||
      messageEvent.data.sessionNonce !== this.sessionNonce
    ) {
      return;
    }
    for (const listener of this.leaseRefreshListeners) listener(messageEvent.data);
  };

  private post(message: unknown): void {
    this.workerTarget()?.postMessage(message);
  }

  private workerTarget(): WorkerTarget | null {
    return (
      this.serviceWorkers?.controller ??
      this.registration?.active ??
      this.registration?.waiting ??
      this.registration?.installing ??
      null
    );
  }

  private postWithAcknowledgement(message: unknown, isCurrent: () => boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      let target: WorkerTarget | null = null;
      let channel: MessageChannel | null = null;
      let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
      let settled = false;

      const clearAttempt = () => {
        if (timer !== undefined) globalThis.clearTimeout(timer);
        timer = undefined;
        channel?.port1.close();
        channel = null;
      };
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearAttempt();
        this.controllerChangeListeners.delete(handleControllerChange);
        if (error) reject(error);
        else resolve();
      };
      const send = () => {
        clearAttempt();
        if (!isCurrent()) {
          finish();
          return;
        }
        target = this.workerTarget();
        if (!target) {
          finish(new Error('mcap_virtual_worker_unavailable'));
          return;
        }

        const nextChannel = new MessageChannel();
        channel = nextChannel;
        timer = globalThis.setTimeout(
          () => finish(new Error('mcap_virtual_grant_ack_timeout')),
          MCAP_VIRTUAL_ACK_TIMEOUT_MS,
        );
        nextChannel.port1.onmessage = (event: MessageEvent<unknown>) => {
          const acknowledgement = event.data as Partial<McapVirtualWorkerAcknowledgement> | null;
          if (acknowledgement?.type === 'vll-mcap-ack' && acknowledgement.accepted === true) {
            finish();
            return;
          }
          finish(new Error('mcap_virtual_grant_rejected'));
        };
        try {
          target.postMessage(message, [nextChannel.port2]);
        } catch (error) {
          finish(
            new Error('mcap_virtual_grant_post_failed', {
              cause: error,
            }),
          );
        }
      };
      const handleControllerChange = () => {
        if (!isCurrent()) {
          finish();
          return;
        }
        const nextTarget = this.workerTarget();
        if (!settled && nextTarget && nextTarget !== target) send();
      };

      this.controllerChangeListeners.add(handleControllerChange);
      send();
    });
  }
}
